import { onBeforeMount, onMounted, ref, type Ref } from 'vue'

import apiTickets from '@/api/apiTickets'
import { useCapitalizedDescriptor } from '@/composables/useCapitalizedDescriptor'
import { useLegalAddress } from '@/composables/useLegalAddress'
import ErrorTrackingService from '@/services/ErrorTrackingService'
import TrackingService from '@/services/TrackingService'
import { ActionType, EventSubtypes } from '@/types/events'
import type { FormPayload } from '@/types/form'

type DescriptorPageState = {
  capitalizedDescriptor: Ref<string>
  legalAddress: Ref<string | null>
  isFormSubmitted: Ref<boolean>
  isLoading: Ref<boolean>
  submittedEmail: Ref<string>
  handleFormSubmit: (data: FormPayload) => Promise<void>
  handleReturnToMainPage: () => void
}

export const useDescriptorPage = (): DescriptorPageState => {
  const capitalizedDescriptor = useCapitalizedDescriptor()
  const legalAddress = useLegalAddress()

  const isFormSubmitted = ref(false)
  const isLoading = ref(false)
  const submittedEmail = ref('')

  async function handleFormSubmit(data: FormPayload): Promise<void> {
    isLoading.value = true

    try {
      await apiTickets.create({
        email: data.email,
        reason: data.reason,
        domain: window.location.hostname,
      })
      submittedEmail.value = data.email
      isFormSubmitted.value = true
    } catch (error) {
      ErrorTrackingService.captureMessage('MainContainer: apiTickets.create() failed in handleFormSubmit', {
        extra: { error },
      })
    } finally {
      isLoading.value = false
    }
  }

  function handleReturnToMainPage(): void {
    if (isLoading.value) {
      return
    }
    isFormSubmitted.value = false
    submittedEmail.value = ''
  }

  onBeforeMount(async () => {
    await TrackingService.init()
  })

  onMounted(async () => {
    const { default: UserIdentityService } = await import('@/services/UserIdentityService')

    await UserIdentityService.initIdVisitorCrossIframe()

    TrackingService.trackComponentInteraction(
      ActionType.Show,
      [],
      EventSubtypes.DescriptorPage,
    )
  })

  return {
    capitalizedDescriptor,
    legalAddress,
    isFormSubmitted,
    isLoading,
    submittedEmail,
    handleFormSubmit,
    handleReturnToMainPage,
  }
}
